import React, { useState, useEffect } from 'react';
import { Button, IconButton, Box, Typography, FormHelperText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Car, Driver } from '../../store/types';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  DialogTextField
} from './styles/Dialog.styles';
import { FormFieldContainer } from './styles/Common.styles';

function isCar(data: Car | Driver): data is Car {
  return 'makeCompany' in data;
}

function isDriver(data: Driver | Car): data is Driver {
  return 'firstName' in data;
}

interface EditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Car | Driver) => void;
  title: string;
  data: Car | Driver | null;
  isNew?: boolean;
  dialogType: 'car' | 'driver';
}

const EditDialog: React.FC<EditDialogProps> = ({
  open,
  onClose,
  onSave,
  title,
  data,
  isNew = false,
  dialogType,
}) => {
  const getInitialData = (): Partial<Car | Driver> => {
    if (isNew) {
      return dialogType === 'car' ? {
        make: '',
        model: '',
        carType: '',
        year: '',
        plateNumber: '',
        capacity: 0,
        fuelType: '',
        mileage: 0,
        bootSpace: 0,
        fastTagId: '',
      } : {
        firstName: '',
        lastName: '',
        mobileNumber: '',
        email: '',
        licenseNumber: '',
      };
    }
    return data || {};
  };

  const getFieldsToShow = () => {
    if (dialogType === 'car') {
      return ['make', 'model', 'carType', 'year', 'plateNumber', 'capacity', 'fuelType', 'mileage', 'bootSpace', 'fastTagId'];
    } else {
      return ['firstName', 'lastName', 'mobileNumber', 'email', 'licenseNumber'];
    }
  };

  const [formData, setFormData] = useState<Partial<Car | Driver>>(getInitialData());
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormData(getInitialData());
    setErrors({});
  }, [data, isNew, dialogType]);

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
    
    // Clear error when field is edited
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const validateDriverForm = () => {
    const newErrors: Record<string, string> = {};
    
    // Type guard to check if formData is a Driver
    const isDriverData = (data: Partial<Car | Driver>): data is Partial<Driver> => {
      return 'firstName' in data || 'lastName' in data || 'mobileNumber' in data;
    };
    
    if (isDriverData(formData)) {
      // Validate required fields
      if (!formData.firstName) newErrors.firstName = 'First name is required';
      if (!formData.lastName) newErrors.lastName = 'Last name is required';
      
      // Validate mobile number (must be 10 digits)
      if (!formData.mobileNumber) {
        newErrors.mobileNumber = 'Mobile number is required';
      } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
        newErrors.mobileNumber = 'Mobile number must be 10 digits';
      }
      
      // Validate email (required and format)
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
      
      // Validate license number
      if (!formData.licenseNumber) {
        newErrors.licenseNumber = 'License number is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (dialogType === 'car' && Object.keys(formData).length > 0) {
      onSave(formData as Car);
      onClose();
    } else if (dialogType === 'driver' && Object.keys(formData).length > 0) {
      // Type guard to check if formData is a Driver
      const isDriver = (data: Partial<Car | Driver>): data is Partial<Driver> => {
        return 'firstName' in data || 'lastName' in data || 'mobileNumber' in data;
      };
      
      if (isDriver(formData)) {
        // Validate driver form before saving
        if (validateDriverForm()) {
          // Format driver data according to the required structure
          const driverData = {
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            mobileNumber: formData.mobileNumber || '',
            email: formData.email || '',
            licenseNumber: formData.licenseNumber || ''
          };
          onSave(driverData as Driver);
          onClose();
        }
      }
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {getFieldsToShow().map((key) => (
            <FormFieldContainer key={key}>
              <Typography variant="caption" sx={{ color: '#888', mb: 1, textTransform: 'capitalize' }}>
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </Typography>
              <DialogTextField
                fullWidth
                size="small"
                value={formData[key as keyof (Car | Driver)] || ''}
                onChange={handleChange(key)}
                variant="outlined"
                error={Boolean(errors[key])}
              />
              {errors[key] && (
                <FormHelperText error>{errors[key]}</FormHelperText>
              )}
            </FormFieldContainer>
          ))}
        </Box>
      </StyledDialogContent>
      <StyledDialogActions>
        <Button 
          onClick={onClose}
          sx={{ 
            color: '#ffffff',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          sx={{ 
            backgroundColor: '#007acc',
            '&:hover': {
              backgroundColor: '#005999',
            },
          }}
        >
          Save
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default EditDialog;
